import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TimeTableInterface } from 'src/app/models/TimeTableModel';
import { TimetableService } from 'src/app/services/timetable/timetable.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-time-table-form',
  templateUrl: './time-table-form.component.html',
  styleUrls: ['./time-table-form.component.css']
})
export class TimeTableFormComponent {

  form: FormGroup;
  apiUrl: string = environment.apiLink;

  @Input() receivedTrip!: TimeTableInterface | null;
  @Output() closeForm = new EventEmitter<void>();
  @Output() dataUpdated = new EventEmitter<void>();


  constructor(
    private formBuilder: FormBuilder,
    private timeTableService: TimetableService,
  ) {
    this.form = this.formBuilder.group({
      trip: ["standart"], //valueChanges não é disparado pelo valor inicial do controle
      viagem: [null, [Validators.required]],
      tipo: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(20)]],
      destino: [null],
      inicioLCB: [null, [Validators.required, Validators.maxLength(5)]],
      saidaLCB: [null, [Validators.required, Validators.maxLength(5)]],
      inicioSLA: [null, [Validators.maxLength(5)]],
      saidaSLA: [null, [Validators.maxLength(5)]],
      extra: [null]
    })
  }

  ngOnInit() {
    this.tripTypeCheck()
    this.handleTripTypeChange()
  }

  ngOnChanges() {
    if (this.receivedTrip) {
      this.updateFormValues(this.receivedTrip)
      this.checkAndUpdateTripType(this.receivedTrip)
      this.handleTripTypeChange()
    } else this.resetForm()
  }

  // preenchimento do formulário na operação edit
  private updateFormValues(trip: TimeTableInterface) {
    this.form.patchValue(trip)
    this.form.controls['viagem'].disable()
  }

  private checkAndUpdateTripType(trip: TimeTableInterface) {
    if (this.isExtraTrip(trip)) {
      this.form.get('trip')?.setValue('extra')
    }
  }

  private handleTripTypeChange() {
    const selectedValue = this.form.get('trip')?.value;

    if (selectedValue === 'extra') {
      this.toggleControls(['inicioLCB', 'inicioSLA', 'saidaLCB', 'saidaSLA'], false)
      this.form.controls['extra'].enable()
    } else {
      this.toggleControls(['inicioLCB', 'saidaLCB'], true, [
        Validators.required,
        Validators.maxLength(5)
      ])

      this.form.controls['extra'].disable()
      this.form.controls['extra'].reset()
    }
  }

  private tripTypeCheck() {
    this.form.get('trip')?.valueChanges.subscribe(selectedValue => {
      this.updateValidatorsBasedOnTripType(selectedValue);
    });
  }

  private isExtraTrip(trip: TimeTableInterface): boolean {
    return trip.extra != null
  }

  private updateValidatorsBasedOnTripType(tripType: string) {
    if (tripType === 'extra') {
      this.disableAndClearValidators(['inicioLCB', 'inicioSLA', 'saidaLCB', 'saidaSLA'])
      this.form.controls['extra'].enable();
    } else {
      this.enableAndSetValidatorsForStandartTrip()
      this.form.controls['extra'].disable();
      this.form.controls['extra'].reset();
    }

    this.form.updateValueAndValidity()
  }

  private enableAndSetValidatorsForStandartTrip() {
    this.enableAndSetValidators('inicioLCB', [Validators.required, Validators.maxLength(5)])
    this.enableAndSetValidators('saidaLCB', [Validators.required, Validators.maxLength(5)])
    this.enableAndSetValidators('inicioSLA', [Validators.maxLength(5)])
    this.enableAndSetValidators('saidaSLA', [Validators.maxLength(5)])
  }

  private toggleControls(controlNames: string[], enable: boolean, validators: any[] = []) {
    controlNames.forEach(controlName => {
      const control = this.form.get(controlName)
      if (enable) {
        control?.enable()
        control?.setValidators(validators)
      } else {
        control?.disable()
        control?.clearValidators()
        control?.reset()
      }
      control?.updateValueAndValidity()
    })
  }

  disableAndClearValidators(controlNames: string[]) {
    this.toggleControls(controlNames, false)
  }

  enableAndSetValidators(controlName: string, validators: any[]) {
    this.toggleControls([controlName], true, validators)
  }

  submitForm() {
    if (this.form.valid) {
      // Criar uma cópia dos valores do formulário sem o controle 'tipo'
      const formData = { ...this.form.value };
      delete formData.trip;

      this.timeTableService.postTrip(formData).subscribe({
        next: () => {
          alert("Dados inseridos com sucesso");
          this.resetForm();
          this.dataUpdated.emit()
        },
        error: (error) => {
          console.error("Erro ao enviar os dados: ", error);
          alert("Erro ao inserir dados");
        },
      });
    } else {
      alert("Preencha os campos corretamente");
    }
  }

  updateTrip() {
    if (this.form.valid) {

      const updatedTrip = this.form.value
      updatedTrip.viagem = this.form.get('viagem')?.value;
      delete updatedTrip.trip
      this.timeTableService.updateTrip(updatedTrip).subscribe({
        next: () => {
          alert("Viagem atualizada");
          this.resetForm();
          this.dataUpdated.emit()
        },
        error: (error) => {
          console.error("Erro ao atualizar dados: ", error);
          alert("Erro ao atualizar viagem");
        },
      });
    } else {
      alert("Preencha os campos corretamente");
    }
  }

  resetForm() {
    this.form.reset({
      trip: "standart", // Valor inicial
      viagem: null,
      tipo: null,
      destino: null,
      inicioLCB: null,
      saidaLCB: null,
      inicioSLA: null,
      saidaSLA: null,
      extra: null,
    });
  }

  onClose() {
    this.resetForm()
    this.closeForm.emit()
  }

  isNewTrip(): boolean {
    return this.receivedTrip == null;
  }

  formatTime(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remove caracteres não numéricos

    if (value.length >= 3) {
      value = value.substring(0, 2) + ':' + value.substring(2, 4); // Adiciona ":"
    }

    input.value = value.substring(0, 5); // Limita ao formato HH:mm
    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.form.controls[controlName].setValue(value);
    }
  }

}
